import type { InvestigationReportData } from '../types';
import { normalizeInvestigationReport } from './normalizeReport';
import { formatIstDateTime, hasValidSreTimestamp } from './formatLocalDateTime';

/** Convert a structured report into markdown suitable for Pulse work notes. */
export function formatReportAsMarkdown(report: InvestigationReportData): string {
  const r = normalizeInvestigationReport(report as InvestigationReportData & Record<string, unknown>);
  const lines: string[] = [];

  lines.push('## SRE Investigation Report');
  lines.push('');
  lines.push(`**Title:** ${r.title}`);
  if (r.severity || r.status) {
    lines.push(
      `**Severity:** ${r.severity || 'n/a'} | **Status:** ${String(r.status || 'n/a').replace(/_/g, ' ')}`
    );
  }
  lines.push('');

  if (r.affected_services?.length) {
    lines.push(`**Affected:** ${r.affected_services.join(', ')}`);
    lines.push('');
  }

  if (r.executive_summary) {
    lines.push('### Executive Summary');
    lines.push(r.executive_summary);
    lines.push('');
  }

  if (r.impact) {
    lines.push('### Impact');
    if (r.impact.user_facing) lines.push(`- **User-facing:** ${r.impact.user_facing}`);
    if (r.impact.service_impact) lines.push(`- **Service:** ${r.impact.service_impact}`);
    if (r.impact.blast_radius) lines.push(`- **Blast radius:** ${r.impact.blast_radius}`);
    lines.push('');
  }

  const timeline = (r.timeline || []).filter((entry) => hasValidSreTimestamp(entry.time));
  if (timeline.length) {
    lines.push('### Timeline');
    for (const entry of timeline) {
      const when = formatIstDateTime(entry.time);
      if (!when) continue;
      lines.push(`- \`${when}\` — ${entry.event}`);
    }
    lines.push('');
  }

  if (r.root_cause?.summary) {
    lines.push(
      `### Root Cause${r.root_cause.confidence ? ` (${r.root_cause.confidence})` : ''}`
    );
    lines.push(r.root_cause.summary);
    if (r.root_cause.details) {
      lines.push('');
      lines.push(r.root_cause.details);
    }
    lines.push('');
  }

  if (r.action_items?.length) {
    lines.push('### Recommended Actions');
    r.action_items.forEach((item, i) => {
      lines.push(
        `${i + 1}. **${String(item.priority).replace(/_/g, ' ')}:** ${item.action}`
      );
    });
    lines.push('');
  }

  if (r.lessons_learned?.length) {
    lines.push('### Lessons Learned');
    for (const lesson of r.lessons_learned) {
      lines.push(`- ${lesson}`);
    }
    lines.push('');
  }

  lines.push('_Generated by Pulse SRE Investigation (Phase A)_');
  return lines.join('\n');
}
